import { ApplicationFormData, allVisionConditions } from "@/api/application/application";
import { hasSubdivisions } from "@/components/UI/forms/SubdivisionSelect";
import { isAtLeastAge } from "./age";
import z from "zod";

export type generalError =
  | "applicant_type_missing"
  | "proxy_country_missing"
  | "proxy_state_missing"
  | "proxy_date_of_birth_missing"
  | "proxy_first_name_missing"
  | "proxy_not_adult"
  | "proxy_last_name_missing"
  | "proxy_phone_missing"
  | "patient_first_name_missing"
  | "patient_last_name_missing"
  | "patient_date_of_birth_missing"
  | "patient_not_adult"
  | "patient_country_missing"
  | "patient_state_missing"
  | "patient_phone_missing"
  | "patient_email_malformed"
  | "travel_willing_missing";
export type consentError =
  | "health_collection_consent_missing"
  | "contact_consent_missing"
  | "contact_email_consent_missing"
  | "health_sharing_consent_missing";
export type medicalError =
  | "vision_conditions_missing"
  | "vision_macular_degeneration_type_missing"
  | "vision_other_details_missing"
  | "vision_acuity_known_missing"
  | "vision_acuity_left_missing"
  | "vision_acuity_left_not_numeric"
  | "vision_acuity_right_missing"
  | "vision_acuity_right_not_numeric"
  | "vision_can_read_normal_print_missing"
  | "vision_can_read_headlines_missing"
  | "vision_can_see_indoors_missing"
  | "vision_can_localize_person_missing"
  | "vision_driving_missing";

function isEmailValid(email: string): boolean {
  const result = z.string().email().safeParse(email);
  return result.success && result.data === email;
}

function isNumeric(value: string | null | undefined): boolean {
  if (!value) return false;
  return !isNaN(Number(value));
}

// Fields made required after applications could already exist (see db/migrate
// and test-integration/specs/application/legacy-applications.spec.ts, which
// seeds the pre-migration row shape for each one). A pre-existing
// locked/submitted application has no way to go back and answer these, so they
// are exempted from blocking a step nobody can edit anymore. New required
// fields should be added here, not to a blanket bypass, so a genuine
// data-loss bug on a longstanding field still surfaces.
const legacyExemptErrors: generalError[] = [
  "patient_country_missing",
  "patient_phone_missing",
  "proxy_date_of_birth_missing",
  "proxy_state_missing",
  "patient_state_missing",
  "travel_willing_missing",
];

export function generalCompletionErrors(app: Partial<ApplicationFormData>): generalError[] {
  const errors: generalError[] = [];

  if (!app.applicant_type) errors.push("applicant_type_missing");

  if (app.applicant_type === "proxy") {
    if (!app.proxy_country) errors.push("proxy_country_missing");
    if (hasSubdivisions(app.proxy_country) && !app.proxy_state) errors.push("proxy_state_missing");
    if (!app.proxy_date_of_birth) errors.push("proxy_date_of_birth_missing");
    if (app.proxy_date_of_birth && !isAtLeastAge(app.proxy_date_of_birth, 18))
      errors.push("proxy_not_adult");
    if (!app.proxy_first_name) errors.push("proxy_first_name_missing");
    if (!app.proxy_last_name) errors.push("proxy_last_name_missing");
    if (!app.proxy_phone) errors.push("proxy_phone_missing");
  }

  if (!app.patient_first_name) errors.push("patient_first_name_missing");
  if (!app.patient_last_name) errors.push("patient_last_name_missing");
  if (!app.patient_date_of_birth) errors.push("patient_date_of_birth_missing");
  if (app.patient_date_of_birth && !isAtLeastAge(app.patient_date_of_birth, 18))
    errors.push("patient_not_adult");
  if (!app.patient_country) errors.push("patient_country_missing");
  if (hasSubdivisions(app.patient_country) && !app.patient_state)
    errors.push("patient_state_missing");
  if (!app.patient_phone) errors.push("patient_phone_missing");
  if (app.personal_patient_email && !isEmailValid(app.personal_patient_email))
    errors.push("patient_email_malformed");
  // Yes/no question, so only a null/undefined value counts as unanswered.
  if (app.personal_travel_willing === null || app.personal_travel_willing === undefined)
    errors.push("travel_willing_missing");

  // The general step renders read-only once consent is locked or the
  // application is submitted, so nothing reported here can be acted on other
  // than fields that predate the application entirely.
  if (app.consent_locked || app.is_submitted) {
    return errors.filter((error) => !legacyExemptErrors.includes(error));
  }

  return errors;
}

export function consentCompletionErrors(app: Partial<ApplicationFormData>): consentError[] {
  const errors: consentError[] = [];

  if (!app.consent_health_collection) errors.push("health_collection_consent_missing");
  if (!app.consent_contact) errors.push("contact_consent_missing");
  if (!app.consent_contact_email) errors.push("contact_email_consent_missing");
  if (!app.consent_health_sharing) errors.push("health_sharing_consent_missing");

  return errors;
}

export function medicalCompletionErrors(app: Partial<ApplicationFormData>): medicalError[] {
  const errors: medicalError[] = [];

  if (!app.vision_conditions || app.vision_conditions.length === 0)
    errors.push("vision_conditions_missing");

  if (
    app.vision_conditions?.includes(allVisionConditions.AGE_RELATED_MACULAR_DEGENERATION) &&
    !app.vision_macular_degeneration_type
  )
    errors.push("vision_macular_degeneration_type_missing");

  if (app.vision_conditions?.includes(allVisionConditions.OTHER) && !app.vision_other_details)
    errors.push("vision_other_details_missing");

  if (app.vision_acuity_known === null || app.vision_acuity_known === undefined)
    errors.push("vision_acuity_known_missing");
  else if (app.vision_acuity_known === true) {
    if (app.vision_acuity_left === null || app.vision_acuity_left === undefined)
      errors.push("vision_acuity_left_missing");
    else if (!isNumeric(app.vision_acuity_left)) errors.push("vision_acuity_left_not_numeric");
    if (app.vision_acuity_right === null || app.vision_acuity_right === undefined)
      errors.push("vision_acuity_right_missing");
    else if (!isNumeric(app.vision_acuity_right)) errors.push("vision_acuity_right_not_numeric");
  } else {
    if (app.vision_can_read_normal_print === null || app.vision_can_read_normal_print === undefined)
      errors.push("vision_can_read_normal_print_missing");
    if (app.vision_can_read_headlines === null || app.vision_can_read_headlines === undefined)
      errors.push("vision_can_read_headlines_missing");
    if (app.vision_can_see_indoors === null || app.vision_can_see_indoors === undefined)
      errors.push("vision_can_see_indoors_missing");
    if (app.vision_can_localize_person === null || app.vision_can_localize_person === undefined)
      errors.push("vision_can_localize_person_missing");
  }

  if (app.vision_driving === null || app.vision_driving === undefined)
    errors.push("vision_driving_missing");

  return errors;
}

export function stepCompletion(app: ApplicationFormData | null | undefined): {
  general: boolean;
  consent: boolean;
  medical: boolean;
  submitted: boolean;
} {
  if (!app)
    return {
      general: false,
      consent: false,
      medical: false,
      submitted: false,
    };

  if (app.is_submitted)
    return {
      general: true,
      consent: true,
      medical: true,
      submitted: true,
    };

  return {
    general: generalCompletionErrors(app).length === 0,
    consent: !!app.consent_locked,
    medical: medicalCompletionErrors(app).length === 0,
    submitted: false,
  };
}
